import { ReactElement, useEffect, useMemo } from "react";

import { isDevMode } from "../../helpers/helpers.ts";
import { shouldUseStep } from "../../helpers/steps.ts";
import { useBooking } from "../../providers/BookingProvider.tsx";
import { useVehicleSelection } from "../../providers/VehicleSelectionContext.tsx";
import DropdownLarge from "../elements/DropdownLarge.tsx";
import Header from "../elements/Header.tsx";
import { Step } from "./Step.tsx";

export const Sidebar = () => {
  const {
    selectedMakeModelId,
    selectedModel,
    selectedVehiclePrettyName,
    selectedVariants,
    selectedGvmWeight: weight,
    handleGvmWeightChange: setWeight,
    selectedGvmUpgrade,
    orderMode,
    selectedOptions,
    availableGvmUpgrades,
    handleGvmUpgradeChange,
    steps,
    availableVariants,
    handleVariantChange,
    notes,
    handleNotesChange,
  } = useVehicleSelection();

  const { isLoading } = useBooking();

  const weights = useMemo(() => {
    return availableGvmUpgrades.map(([weight]) => {
      return {
        label: weight + " kg GVM",
        value: weight,
      };
    });
  }, [availableGvmUpgrades]);

  const upgrades = useMemo(() => {
    if (!weight) {
      return [];
    }

    return availableGvmUpgrades
      .filter(([w]) => w === weight)
      .flatMap(([, products]) => products)
      .map((product) => ({
        label: product.name,
        value: product.id,
      }));
  }, [weight, availableGvmUpgrades]);

  useEffect(() => {
    if (!weight) {
      return;
    }

    if (upgrades.length === 1) {
      handleGvmUpgradeChange(upgrades[0].value);
    } else {
      handleGvmUpgradeChange(null);
    }
  }, [upgrades, weight, handleGvmUpgradeChange]);

  const comingSoonText = useMemo((): ReactElement[] => {
    if (weights.length > 0) {
      return [];
    }

    const selections = [selectedModel, ...selectedVariants]
      .filter((v) => v !== null)
      .reverse();

    for (const selection of selections) {
      if (selection.acf.gvm_coming_soon_text) {
        return selection.acf.gvm_coming_soon_text
          .split("\n")
          .map((line, i) => <div key={i}>{line}</div>);
      }
    }

    return [
      <div key={0}>
        There are currently no GVM upgrade kits available for the selected
        vehicle, but they are coming soon.
      </div>,
    ];
  }, [selectedModel, selectedVariants, weights.length]);

  const maxWeight = useMemo(() => {
    return weights.length > 0
      ? Math.max(...weights.map((w) => Number(w.value)))
      : undefined;
  }, [weights]);

  const displayWeight = useMemo(() => {
    if (!maxWeight && !weight) {
      return null;
    }
    if (!weight || !weights.find((w) => w.value === weight)) {
      return (
        <>
          up to <strong>{maxWeight}kg</strong>
        </>
      );
    }
    return (
      <>
        to <strong>{weight}kg</strong>
      </>
    );
  }, [weight, maxWeight, weights]);

  const registrationEntry = useMemo(() => {
    for (const step of steps) {
      const entry = step.entries.find(
        (entry) => entry.entry_name === "Pre or Post Rego",
      );
      if (entry) {
        return entry;
      }
    }

    return undefined;
  }, [steps]);

  const preRegistrationAvailable =
    registrationEntry?.products.some(
      (option) => option.option_name === "Pre Rego",
    ) ?? false;
  const postRegistrationAvailable =
    registrationEntry?.products.some(
      (option) => option.option_name === "Post Rego",
    ) ?? false;

  return (
    <div className="md:min-w-[400px] md:max-w-[400px] flex flex-col gap-4 px-4 lg:px-0">
      <div>
        {selectedVehiclePrettyName && (
          <>
            <div className="sidebar-heading font-poppins text-[28px] font-bold uppercase">
              {selectedVehiclePrettyName}
            </div>
            {displayWeight && (
              <div className="flex items-center gap-2 mt-2 font-montserrat text-normal">
                <img src="/images/kg-icon.png" alt="KG" className="w-7 h-7" />
                <span>GVM increase {displayWeight}</span>
              </div>
            )}

            {(preRegistrationAvailable || postRegistrationAvailable) && (
              <div className="mb-5 mt-2 font-montserrat text-normal">
                {preRegistrationAvailable && (
                  <div className="flex items-center gap-2 mb-1">
                    <img
                      src="/images/check-icon.png"
                      alt="Check"
                      className="w-7 h-7"
                    />
                    <span>Pre Registration</span>
                  </div>
                )}
                {postRegistrationAvailable && (
                  <div className="flex items-center gap-2">
                    <img
                      src="/images/check-icon.png"
                      alt="Check"
                      className="w-7 h-7"
                    />
                    <span>Post Registration*</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="lg:hidden xl:block">
        <Header size="2xl" hideUnderline>
          {selectedVehiclePrettyName}
        </Header>
      </div>

      {availableVariants.length > 1 && availableVariants[1] && (
        <DropdownLarge
          options={availableVariants[1].values.map((variant) => ({
            label: variant.name,
            value: variant.id.toString(),
          }))}
          selected={selectedVariants[1]?.id ?? undefined}
          onChange={(value) => {
            handleVariantChange(1, Number(value));
          }}
          label={"Select " + availableVariants[1].label}
        />
      )}

      {isLoading && (
        <div className="text-center rounded bg-gray-200 px-4 py-6 font-montserrat text-lg">
          Loading GVM upgrades...
        </div>
      )}

      {weights.length > 0 && (
        <DropdownLarge
          options={weights}
          label={"GVM Weight"}
          required={true}
          selected={Number(weight) || undefined}
          onChange={(value) => {
            setWeight(value + "");
          }}
        />
      )}

      {!isLoading && weights.length === 0 && selectedMakeModelId && (
        <div className="text-center rounded bg-red-200 px-4 py-6 font-montserrat text-lg gap-4 flex flex-col">
          {comingSoonText}
          <div>
            In the meantime, you can check out our range of{" "}
            <a href={"/shop"} className="underline">
              parts and accessories
            </a>
            .
          </div>
        </div>
      )}

      {weights.length > 0 && !weight && (
        <div className="text-center rounded bg-primary text-white px-4 py-6 font-montserrat text-lg gap-4 flex flex-col">
          <div>
            Select your desired GVM weight above to view compatible upgrades.
          </div>
        </div>
      )}

      {upgrades.length > 0 && (
        <DropdownLarge
          options={upgrades}
          label={"GVM Upgrade"}
          required={true}
          selected={selectedGvmUpgrade?.id}
          onChange={(value) => {
            handleGvmUpgradeChange(Number(value));
          }}
        />
      )}

      {isDevMode && selectedGvmUpgrade && (
        <div className="flex justify-between bg-yellow-200 p-3">
          #{selectedGvmUpgrade.id}
          <a
            href={
              import.meta.env.VITE_JMACX_WORDPRESS_URL +
              "/wp-admin/post.php?post=" +
              selectedGvmUpgrade.id +
              "&action=edit"
            }
            className="underline text-blue-500 hover:text-blue-700"
            target={"_blank"}
            rel="noreferrer"
          >
            Edit product
          </a>
          <a
            href={"https://jmacx.com.au/?p=" + selectedGvmUpgrade.id}
            className="underline text-blue-500 hover:text-blue-700"
            target={"_blank"}
            rel="noreferrer"
          >
            View on old site
          </a>
        </div>
      )}

      {selectedGvmUpgrade && !selectedGvmUpgrade.is_purchasable && (
        <div className="text-center rounded bg-red-200 p-3">
          <div>
            The selected GVM upgrade is not available for you to purchase. You
            can view the options but will be unable to add this upgrade to your
            cart.
          </div>
        </div>
      )}

      {steps.map((step, index) =>
        shouldUseStep(step, orderMode, selectedOptions) ? (
          <Step step={step} index={index} key={index} />
        ) : null,
      )}

      {selectedGvmUpgrade && (
        <div>
          <div className="font-bebasneue text-[20px] font-normal leading-[110%] tracking-[0.25px] underline flex justify-between items-center mb-3">
            Additional Notes
          </div>
          <textarea
            className="bg-white! w-full border border-gray-300 rounded p-3 font-montserrat text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            rows={4}
            placeholder="Add any additional notes here..."
            value={notes}
            onChange={(e) => handleNotesChange(e.target.value)}
          />
        </div>
      )}
    </div>
  );
};
